/**
 * The sender's side of an interpreter-to-interpreter transfer
 * (docs/interpreter-transfer-handover.md), shared by the web Tools panel's
 * Transfer section (components/web/TransferSection.tsx) and Malka Terp's call
 * tools (vrs-layout/components/InterpreterCallTools.native.tsx).
 *
 * - Colleagues come from GET /api/interpreter/transfer-candidates?callId=, so
 *   only those the server would let take this call are listed, by name.
 * - An offer is pending until the server says how it ended:
 *   call_transfer_pending (then it may be withdrawn), call_transfer_cancelled
 *   (declined, expired, unavailable, or withdrawn), a refusal (error with
 *   operation 'call_transfer' and this call), or call_transfer_accepted. The
 *   server withdraws an unanswered offer after 45 s; if its word never comes,
 *   the panel frees itself rather than wait on disabled buttons.
 * - Accepted, the call is the colleague's: the server releases this
 *   interpreter once they are in (interpreter-queue/middleware.ts).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { callTransferAcceptedPayloadSchema, callTransferCancelledPayloadSchema, callTransferPendingPayloadSchema } from '../../../contracts/queue';
import { timeoutPromise } from '../base/util/timeoutPromise';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { transferTakingOverText } from '../interpreter-queue/transferOffers';
import { apiClient } from '../shared/api-client';
const candidatesSchema = z.object({ candidates: z.array(z.object({
        id: z.string().min(1).max(120),
        name: z.string().max(200).nullable().optional(),
        languages: z.array(z.string()).optional()
    })) });
// No word from the server on an offer sent this long ago: it is not pending.
export const TRANSFER_SEND_TIMEOUT_MS = 15000;
// Past the offer's own expiry, the server's word on it is overdue.
export const TRANSFER_ANSWER_GRACE_MS = 10000;
// An offer from a server that names no expiry is given up after this long.
export const TRANSFER_ANSWER_FALLBACK_MS = 60000;
/** A colleague's name, or a neutral stand-in when the server has none. */
export function candidateName(candidate) {
    return candidate.name?.trim() || 'Interpreter';
}
/** "Sam · ASL, LSQ" */
export function candidateLabel(candidate) {
    const languages = candidate.languages?.filter(Boolean).join(', ');
    return languages ? `${candidateName(candidate)} · ${languages}` : candidateName(candidate);
}
/** Short words for a refused offer, from the server's code. */
export function transferRefusalText(code, name) {
    switch (code) {
        case 'TARGET_OFFLINE':
            return `${name} is offline.`;
        case 'TRANSFER_TARGET_NOT_FOUND':
        case 'TRANSFER_TARGET_LANGUAGE_MISMATCH':
        case 'TRANSFER_TARGET_SERVICE_MODE_MISMATCH':
        case 'TRANSFER_TARGET_DEMO_ONLY':
        case 'SERVICE_REGION_NOT_COVERED':
            return `${name} can't take this call.`;
        case 'CALL_ENDED':
            return 'Call ended.';
        case 'TRANSFER_NOT_CALL_INTERPRETER':
            return 'This call is no longer yours.';
        case 'DEMO_OPERATION_UNAVAILABLE':
            return "Demo calls can't be transferred.";
        default:
            return 'Transfer failed. Try again.';
    }
}
/** What an ended offer leaves on the panel; nothing after the sender's own withdrawal. */
export function transferCancelledText(reason, name) {
    switch (reason) {
        case 'withdrawn':
            return '';
        case 'expired':
            return `No answer from ${name}.`;
        case 'unavailable':
            return `${name} can't take this call.`;
        default:
            return `${name} declined.`;
    }
}
// Refusals that come without an operation, answering the offer just sent.
const UNTAGGED_SEND_REFUSALS = new Set(['DEMO_OPERATION_UNAVAILABLE', 'INTERNAL_ERROR', 'VALIDATION_ERROR']);
async function boundedRequest(request, signal) {
    let onAbort = () => { };
    const cancelled = new Promise((_resolve, reject) => {
        onAbort = () => reject(new Error('Transfer request cancelled'));
        signal.addEventListener('abort', onAbort, { once: true });
        if (signal.aborted)
            onAbort();
    });
    try {
        return await timeoutPromise(Promise.race([request, cancelled]), 15000);
    }
    finally {
        signal.removeEventListener('abort', onAbort);
    }
}
function isRecord(value) {
    return Boolean(value) && typeof value === 'object';
}
/**
 * @param input.account - This interpreter's account key (a change starts over).
 * @param input.callId - The call in this room, or none.
 * @param input.open - Whether the panel is showing (colleagues load then).
 * @param input.currentAccount - Reads the signed-in account now, for late answers.
 */
export default function useCallTransfer({ account, callId, open, currentAccount }) {
    const owner = useMemo(() => ({ account, callId }), [account, callId]);
    const currentOwner = useRef(owner);
    currentOwner.current = owner;
    const [list, setList] = useState({ owner: null, rows: [], status: 'idle' });
    const load = useRef(null);
    const pendingRef = useRef(null);
    const [pending, setPendingState] = useState(null);
    const [result, setResult] = useState(null);
    const timer = useRef(null);
    const clearTimer = useCallback(() => {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = null;
    }, []);
    const setPending = useCallback((next) => {
        pendingRef.current = next;
        setPendingState(next);
    }, []);
    const owns = useCallback((target) => currentOwner.current === target && Boolean(target.account)
        && currentAccount() === target.account, [currentAccount]);
    const finish = useCallback((target, text, handedOver = false) => {
        clearTimer();
        setPending(null);
        setResult({ owner: target, text, handedOver });
    }, [clearTimer, setPending]);
    // The server's word on this offer is overdue past its expiry (or, from a
    // server that names none, a minute): take it back and free the panel.
    const armAnswerTimer = useCallback((offer, expiresAt) => {
        clearTimer();
        const expires = expiresAt ? Date.parse(expiresAt) : Number.NaN;
        const delay = Number.isFinite(expires)
            ? Math.max(0, expires - Date.now()) + TRANSFER_ANSWER_GRACE_MS : TRANSFER_ANSWER_FALLBACK_MS;
        timer.current = setTimeout(() => {
            const now = pendingRef.current;
            if (!now || now.owner !== offer.owner || now.transferId !== offer.transferId || !owns(offer.owner))
                return;
            if (offer.transferId)
                queueService.withdrawCallTransfer(offer.transferId);
            finish(offer.owner, `No answer from ${offer.name}.`);
        }, delay);
    }, [clearTimer, finish, owns]);
    const reload = useCallback(() => {
        load.current?.abort();
        if (!owner.account || !owner.callId) {
            setList({ owner, rows: [], status: 'idle' });
            return;
        }
        const controller = new AbortController();
        load.current = controller;
        const mine = () => owns(owner) && load.current === controller && !controller.signal.aborted;
        setList({ owner, rows: [], status: 'loading' });
        void boundedRequest(apiClient.get(`/api/interpreter/transfer-candidates?callId=${encodeURIComponent(owner.callId)}`, { signal: controller.signal, retryAuth: false }), controller.signal)
            .then(response => {
            const parsed = candidatesSchema.safeParse(response.data);
            if (response.error || response.status < 200 || response.status >= 300 || !parsed.success) {
                throw new Error('Candidates unavailable');
            }
            if (mine())
                setList({ owner, rows: parsed.data.candidates, status: 'ready' });
        })
            .catch(() => {
            if (mine())
                setList({ owner, rows: [], status: 'failed' });
        })
            .finally(() => controller.abort());
    }, [owner, owns]);
    useEffect(() => {
        if (open)
            reload();
        return () => {
            load.current?.abort();
            load.current = null;
        };
    }, [open, reload]);
    useEffect(() => {
        setPending(null);
        setResult(null);
        const forThisCall = (data) => Boolean(owner.callId) && owns(owner)
            && isRecord(data) && data.callId === owner.callId;
        const current = () => (pendingRef.current?.owner === owner ? pendingRef.current : null);
        const onPending = (data) => {
            const parsed = callTransferPendingPayloadSchema.safeParse(data);
            const offer = current();
            if (!parsed.success || !forThisCall(data) || !offer || offer.phase !== 'sending')
                return;
            const next = { ...offer, name: parsed.data.toInterpreterName?.trim() || offer.name,
                phase: 'waiting', transferId: parsed.data.transferId };
            setPending(next);
            armAnswerTimer(next, parsed.data.expiresAt);
        };
        const onAccepted = (data) => {
            const parsed = callTransferAcceptedPayloadSchema.safeParse(data);
            if (!parsed.success || !forThisCall(data))
                return;
            finish(owner, transferTakingOverText(parsed.data.acceptedByName || current()?.name), true);
        };
        const onCancelled = (data) => {
            const parsed = callTransferCancelledPayloadSchema.safeParse(data);
            const offer = current();
            if (!parsed.success || !forThisCall(data) || !offer
                || (offer.transferId && offer.transferId !== parsed.data.transferId))
                return;
            finish(owner, transferCancelledText(parsed.data.reason, offer.name));
        };
        const onError = (data) => {
            const offer = current();
            if (!offer || !isRecord(data) || !owns(owner))
                return;
            if (data.operation === 'call_transfer_cancel' && data.callId === owner.callId
                && data.code === 'TRANSFER_ALREADY_ACCEPTED') {
                // The colleague got there first; call_transfer_accepted follows.
                const back = { ...offer, phase: 'waiting' };
                setPending(back);
                armAnswerTimer(back);
                return;
            }
            const refusesSend = offer.phase === 'sending' && (data.operation === 'call_transfer'
                ? data.callId === owner.callId : !data.operation && UNTAGGED_SEND_REFUSALS.has(String(data.code)));
            if (refusesSend)
                finish(owner, transferRefusalText(data.code, offer.name));
        };
        queueService.on('callTransferPending', onPending);
        queueService.on('callTransferAccepted', onAccepted);
        queueService.on('callTransferCancelled', onCancelled);
        queueService.on('error', onError);
        return () => {
            clearTimer();
            queueService.off('callTransferPending', onPending);
            queueService.off('callTransferAccepted', onAccepted);
            queueService.off('callTransferCancelled', onCancelled);
            queueService.off('error', onError);
        };
    }, [owner, owns, armAnswerTimer, clearTimer, finish, setPending]);
    const rows = list.owner === owner ? list.rows : [];
    const status = list.owner === owner ? list.status : owner.callId ? 'loading' : 'idle';
    const settled = result?.owner === owner ? result : null;
    const offering = pending?.owner === owner ? pending : null;
    const offer = useCallback((candidate) => {
        if (pendingRef.current || settled?.handedOver || !owner.callId || !owns(owner)
            || list.owner !== owner || list.status !== 'ready' || !list.rows.includes(candidate))
            return;
        const next = { owner, candidateId: candidate.id, name: candidateName(candidate), phase: 'sending' };
        setResult(null);
        setPending(next);
        clearTimer();
        timer.current = setTimeout(() => {
            if (pendingRef.current === next && owns(owner))
                finish(owner, 'Transfer not confirmed. Try again.');
        }, TRANSFER_SEND_TIMEOUT_MS);
        try {
            queueService.transferCall(owner.callId, candidate.id);
        }
        catch {
            // The service may have queued it before throwing: the timeout decides.
        }
    }, [clearTimer, finish, list, owner, owns, setPending, settled?.handedOver]);
    const withdraw = useCallback(() => {
        const now = pendingRef.current;
        if (!now || now.owner !== owner || now.phase !== 'waiting' || !now.transferId || !owns(owner))
            return;
        const next = { ...now, phase: 'withdrawing' };
        setPending(next);
        clearTimer();
        timer.current = setTimeout(() => {
            if (pendingRef.current !== next || !owns(owner))
                return;
            const back = { ...next, phase: 'waiting' };
            setPending(back);
            setResult({ owner, text: 'Withdraw not confirmed.', handedOver: false });
            armAnswerTimer(back);
        }, TRANSFER_SEND_TIMEOUT_MS);
        try {
            queueService.withdrawCallTransfer(now.transferId);
        }
        catch {
            // The timeout above gives the button back.
        }
    }, [armAnswerTimer, clearTimer, owner, owns, setPending]);
    return {
        candidates: rows,
        // Accepted: the call is the colleague's; nothing more to offer.
        handedOver: Boolean(settled?.handedOver),
        // How the last offer ended, or a step that was not confirmed.
        note: settled?.text || '',
        offer,
        pending: offering ? {
            name: offering.name,
            phase: offering.phase,
            text: offering.phase === 'sending' ? `Sending to ${offering.name}…`
                : offering.phase === 'withdrawing' ? 'Withdrawing…' : `Waiting for ${offering.name}`
        } : null,
        reload,
        status,
        withdraw
    };
}
